import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { fetchModel, fetchModelComponents, saveModelComponents,
         fetchCausalLoop, saveCausalLoop,
         fetchSoftSystems, saveSoftSystems } from '../store/modelSlice';
import ModelCanvas from '../components/ModelCanvas';
import CausalLoopCanvas from '../components/CausalLoopCanvas';
import SSMInterface from '../components/SSMInterface';
import BarraLlenc from '../components/BarraLlenc';
import BarraEstocs from '../components/BarraEstocs';
import EncaraNoABM from '../components/EncaraNoABM';
import EnhancedPropertiesPanel from '../components/EnhancedPropertiesPanel';
import socketService from '../services/socketService';
import { comprovaAbansDenviar } from '../services/mapesEstocsFluxos';
import { teContingut } from '../services/mapesSSM';

// Un error del magatzem pot ser **una clau nostra** o **un missatge del servidor**. Es distingeix
// pel prefix: si comença per «errors.» el traduïm, i si no, es pinta tal com arriba —és seu—.
const textDError = (t, e) => (typeof e === 'string' && e.startsWith('errors.') ? t(e) : e);

// EL TIPUS QUE ES POT CREAR I ENCARA NO TÉ EDITOR.
//
// N'hi havia dos. **`hybrid` se n'ha anat del tot el 27/08/2026** —no s'ofereix i l'API el
// rebutja— perquè acoblar dinàmica de sistemes amb agents només val la pena quan les dues
// meitats existeixen, i no n'hi havia cap.
//
// `agent_based` es queda **a la vista i a posta**: la seva pantalla explica què és, què aportaria
// que els altres llenços no poden, per què encara no hi és i com es faria. No és cap façana —no
// fa veure que funciona— sinó una nota que no es perdi.
const SENSE_EDITOR = ['agent_based'];

const ModelEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentModel, loading, savingComponents, componentsError } = useSelector((state) => state.models);
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  // Qui hi ha ara mateix i qui té el llapis. `null` vol dir «encara no ho sé».
  const [sala, setSala] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [mode, setMode] = useState('select');
  const [desatFet, setDesatFet] = useState(false);
  const [problema, setProblema] = useState(null);
  const canvasRef = useRef(null);

  // Els components dels bucles causals i de la SSM viuen a unes altres taules
  // i encara no es desen; el botó només val per als d'estocs i fluxos.
  // Des del 25/08/2026 els bucles causals també es desen. El botó continua desactivat per als
  // Soft Systems, que segueixen sense res al darrere.
  // Des del 26/08/2026 els tres tipus es desen. El botó ja no es desactiva per a cap.
  // NOMÉS LECTURA.
  //
  // Des del 27/08/2026 un instructor pot **veure** el model d'un alumne seu sense poder-lo
  // editar. Fins avui l'editor no ho sabia: obria el llenç igual, amb el botó de desar viu, i
  // prémer-lo donava un 403 sense explicar res. **Un botó que no pot funcionar no s'ha de poder
  // prémer**, i la pantalla ha de dir per què.
  //
  // La regla és la mateixa que la del servidor —`potEditar` a `model.routes.js`— i aquí és
  // comoditat, no protecció: qui protegeix és el 403. Per això no cal que siguin la mateixa
  // funció; el que cal és que si aquesta s'equivoca de menys, el servidor ho aturi igual.
  const potEditar = !!currentModel && (
    user?.role === 'admin' ||
    currentModel.creatorId === user?.id ||
    (currentModel.sharedWith || []).includes(user?.id));

  // EL LLAPIS. Un el té i edita; la resta miren.
  //
  // **Si encara no se sap res de la sala, es deixa editar.** El bloqueig és coordinació i no
  // permís: si el socket no arriba —la xarxa de l'aula, un tallafoc, el servidor reiniciant-se—
  // deixar la gent mirant una pantalla morta seria fer mal amb una cosa que només ha d'evitar
  // que dos companys es trepitgin. Qui protegeix de debò segueix sent el 403.
  const teElLlapis = !sala?.llapis || sala.llapis.id === user?.id;
  const quiEdita = !teElLlapis ? sala.llapis.name : null;

  const esPotDesar = potEditar && teElLlapis && (currentModel?.type === 'stock_flow'
    || currentModel?.type === 'causal_loop' || currentModel?.type === 'soft_systems');

  // XOC: algú ha desat mentre tu treballaves.
  //
  // No és un error tècnic i no s'ha de tractar com si ho fos: la teva feina segueix a la
  // pantalla, la seva segueix desada, i el que cal és **decidir**. Es diu clar i es dona la
  // sortida —tornar a carregar—, que és l'única que no perd res de ningú sense dir-ho.
  const [xoc, setXoc] = useState(false);

  const handleSave = async () => {
    setDesatFet(false);
    setXoc(false);

    // EL SOFT SYSTEMS VA A PART, i no perquè sí: el seu `PUT` desa etapa per etapa i no
    // substitueix el que no s'envia, o sigui que no li cal la pregunta de «desar en buit» que
    // sí que necessiten els altres dos. I «buit» hi vol dir una altra cosa: un SSM amb una
    // descripció escrita ja és feina d'una classe, i comptar dibuixos hi diria que no hi ha res.
    if (currentModel?.type === 'soft_systems') {
      const dades = currentModel?.softSystemsModel || {};
      if (!teContingut(dades) && !window.confirm(t('editor.desarBuit'))) return;
      const r = await dispatch(saveSoftSystems({ modelId: id, dades, basatEn: currentModel.updatedAt }));
      if (r.payload === 'conflicte') return setXoc(true);
      if (saveSoftSystems.fulfilled.match(r)) {
        setProblema(null); setDesatFet(true); setTimeout(() => setDesatFet(false), 3000);
      }
      return;
    }

    const bucles = currentModel?.type === 'causal_loop';

    // DESAR EN BUIT ESBORRA EL QUE HI HA AL SERVIDOR.
    //
    // Tant el `PUT` de components com el de bucles causals substitueixen TOT el que hi ha: si
    // per qualsevol motiu el llenç arriba aquí buit —una càrrega que ha arribat tard, un
    // desplegament a mig fer— prémer «Desar» es carrega la feina desada i no hi ha manera de
    // recuperar-la. Val més preguntar-ho: buidar un model a posta es fa un cop a la vida, i
    // perdre'l sense voler es fa en un clic.
    const quantes = bucles
      ? (currentModel?.causalLoopModel?.variables?.length || 0)
      : (currentModel?.components?.length || 0);
    if (quantes === 0 && !window.confirm(t('editor.desarBuit'))) return;
    // Una fletxa que apunti a un component que no s'envia deixaria un dibuix trencat que només
    // es veuria en tornar-lo a obrir. Es comprova aquí, al client, perquè els extrems viuen dins
    // d'un JSONB i el servidor no els pot validar sense conèixer la notació.
    if (currentModel?.type === 'stock_flow') {
      const problemes = comprovaAbansDenviar(currentModel?.components || []);
      if (problemes.length) { setProblema(t(problemes[0].clau)); return; }
      setProblema(null);
    }

    const accio = bucles ? saveCausalLoop : saveModelComponents;
    const resultat = await dispatch(bucles
      ? accio({
          modelId: id,
          basatEn: currentModel.updatedAt,
          variables: currentModel?.causalLoopModel?.variables || [],
          relationships: currentModel?.causalLoopModel?.relationships || [],
        })
      : accio({ modelId: id, basatEn: currentModel.updatedAt,
                components: currentModel?.components || [] }));
    if (resultat.payload === 'conflicte') return setXoc(true);
    if (accio.fulfilled.match(resultat)) {
      setProblema(null);
      setDesatFet(true);
      socketService.heDesat(id);
      setTimeout(() => setDesatFet(false), 3000);
    }
  };

  const peuPDF = [currentModel?.description, new Date().toLocaleDateString()]
    .filter(Boolean).join(' · ');

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (canvasRef.current) {
      canvasRef.current.setMode(newMode);
    }
  };

  useEffect(() => {
    // L'ordre importa: fetchModel substitueix currentModel sencer, o sigui que
    // els components s'han de demanar quan aquell ja hagi acabat, o quedarien
    // esborrats. Cal unwrap(): un dispatch d'un thunk es resol igualment quan
    // ha fallat, i llavors demanaríem els components d'un model que no s'ha
    // pogut carregar.
    dispatch(fetchModel(id))
      .unwrap()
      .then((m) => {
        // Cada tipus de model desa en un lloc diferent, i demanar-ho tot sempre voldria dir
        // demanar coses buides a cada obertura.
        //
        // `unwrap()` ja torna el model pelat, no l'acció: buscar-hi `.payload` donava undefined
        // i no es demanava mai res. Ho va trobar la prova de fum de l'editor.
        const tipus = m?.type;
        if (tipus === 'causal_loop') return dispatch(fetchCausalLoop(id));
        if (tipus === 'stock_flow') return dispatch(fetchModelComponents(id));
        if (tipus === 'soft_systems') return dispatch(fetchSoftSystems(id));
        return undefined;
      })
      .catch(() => {});
    socketService.connect();
    socketService.onSala(setSala);
    socketService.onLlapisPres(({ per }) => toast(t('colab.telHanPres', { qui: per })));
    socketService.onDesatPerAlgu(({ per }) => toast(t('colab.algúHaDesat', { qui: per })));
    socketService.joinModel(id);

    return () => {
      socketService.leaveModel(id);
      socketService.disconnect();
      setSala(null);
    };
  }, [id, dispatch, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentModel) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">{t('editor.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">{currentModel.name}</h1>
            <p className="text-sm text-gray-500">{t('editor.modelOfType', { type: t(`modelTypes.${currentModel.type}`).toLowerCase() })}</p>
          </div>
          <div className="flex items-center space-x-2">
            {(componentsError || problema) && (
              <span className="text-sm text-danger-600 max-w-md text-right">{problema || textDError(t, componentsError)}</span>
            )}
            {desatFet && !componentsError && (
              <span className="text-sm text-ok-600">{t('editor.saved')}</span>
            )}

            {/* Qui hi és ara mateix. Amb un sol present no es dibuixa: «ets tu» no és
                informació, i una icona que hi és sempre s'aprèn a no mirar. */}
            {sala && sala.presents.length > 1 && (
              <span className="flex items-center -space-x-1.5 mr-1"
                title={sala.presents.map((p) => p.name).join(', ')}>
                {sala.presents.slice(0, 4).map((p) => (
                  <span key={p.id}
                    className={`inline-grid place-items-center w-7 h-7 rounded-full text-[11px]
                                font-semibold text-white ring-2 ring-white ${
                      sala.llapis?.id === p.id ? 'bg-primary-700' : 'bg-gray-400'}`}>
                    {p.name.trim().slice(0, 2).toUpperCase()}
                  </span>
                ))}
              </span>
            )}
            {/* ELS ARQUETIPS, al costat de «Desar» i no dins de la barra del llenç.
                A la barra semblaven **una eina més** —al costat de triar, moure i esborrar— i no
                ho són: no fan res al dibuix, són material de consulta. Aquí a dalt, a l'alçada
                del títol del model, es llegeix com el que és.
                El color d'accent només al text i a la vora: prou per distingir-lo de «Desar»
                sense competir-hi, que l'acció important d'aquesta capçalera segueix sent desar. */}
            {currentModel.type === 'causal_loop' && (
              <Link to="/arquetips" state={{ tornaA: location.pathname }}
                className="flex items-center gap-1.5 px-3 py-1 text-sm rounded-md border
                           border-primary-300 text-primary-800 hover:bg-primary-50">
                <ArrowPathRoundedSquareIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{t('nav.arquetips')}</span>
              </Link>
            )}

            <button
              onClick={handleSave}
              disabled={!esPotDesar || savingComponents}
              title={!potEditar ? t('editor.nomesLecturaTitol')
                     : esPotDesar ? t('editor.saveTitle') : t('editor.saveDisabled')}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingComponents ? t('editor.saving') : t('editor.save')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Quan no es pot editar, dir-ho una vegada i clar. Un llenç que respon al ratolí i
          després no desa res és pitjor que un llenç que avisa. */}
      {!potEditar && (
        <div className="bg-warn-50 border-b border-warn-200 px-4 py-2 text-sm text-warn-900">
          <b>{t('editor.nomesLectura')}</b> · {t('editor.nomesLecturaCos')}
        </div>
      )}

      {xoc && (
        <div className="bg-danger-50 border-b border-danger-200 px-4 py-2 text-sm text-danger-900
                        flex flex-wrap items-center gap-x-3 gap-y-1">
          <span><b>{t('colab.xocTitol')}</b> · {t('colab.xocCos')}</span>
          <button type="button" onClick={() => window.location.reload()}
            className="ml-auto px-3 py-1 text-xs font-semibold rounded-md border border-danger-300
                       bg-white text-danger-900 hover:bg-danger-100">
            {t('colab.tornaACarregar')}
          </button>
        </div>
      )}

      {/* Aquesta és l'altra: pots editar-lo, però ara el té algú altre. Diu QUI, i deixa
          prendre'l — no demanar-lo i esperar: si qui el té ha marxat de davant de la pantalla,
          una petició que ningú no contesta deixa l'equip aturat. */}
      {potEditar && quiEdita && (
        <div className="bg-warn-50 border-b border-warn-200 px-4 py-2 text-sm text-warn-900
                        flex flex-wrap items-center gap-x-3 gap-y-1">
          <span><b>{t('colab.editaAra', { qui: quiEdita })}</b> · {t('colab.editaAraCos')}</span>
          <button type="button" onClick={() => socketService.prenLlapis(id)}
            className="ml-auto px-3 py-1 text-xs font-semibold rounded-md border border-warn-300
                       bg-white text-warn-900 hover:bg-warn-100">
            {t('colab.prenElLlapis')}
          </button>
        </div>
      )}

      {/* DOS TIPUS DE MODEL SENSE EDITOR.
          L'enum `model_type` en té cinc i el selector n'ofereix cinc, però només tres tenen
          pantalla. Fins avui un model `agent_based` o `hybrid` obria el llenç d'estocs i fluxos
          —que no és el que demana— i el botó «Desar» sortia apagat sense dir per què. És el
          mateix defecte que aquest projecte arrossega des del principi: **una pantalla escrita
          abans que el que hi ha al darrere**. Val més que ho digui. */}
      {SENSE_EDITOR.includes(currentModel.type) ? (
        <EncaraNoABM />
      ) : currentModel.type === 'soft_systems' ? (
        // El SSM porta la seva pròpia disposició de dues columnes; només li cal una alçada
        // definida i que no vessi. `min-h-0` és el que fa que els seus `overflow-y-auto` de dins
        // s'activin: sense això, un fill flexible té `min-height: auto` i creix per sota del
        // contenidor en comptes de fer barra de desplaçament.
        <div className="flex-1 min-h-0 flex overflow-hidden">
          <SSMInterface modelId={id} />
        </div>
      ) : currentModel.type === 'causal_loop' ? (
        // El llenç ocupa l'amplada sencera; les propietats surten a sobre, i només quan hi ha
        // alguna cosa seleccionada. Abans hi havia 256 px de panell a l'esquerra i 320 a la
        // dreta, sempre, i els controls de simulació a sota de tot.
        <>
          <BarraLlenc mode={mode} onModeChange={handleModeChange} canvasRef={canvasRef}
            titolModel={currentModel.name} peuPDF={peuPDF} />
          <div className="flex-1 relative overflow-hidden flex">
            <CausalLoopCanvas
              ref={canvasRef}
              modelId={id}
              onModeChange={setMode}
              onSelectComponent={setSelectedComponent}
              selectedComponent={selectedComponent}
            />
            <EnhancedPropertiesPanel
              flotant
              component={selectedComponent}
              onTancar={() => setSelectedComponent(null)}
              onUpdateComponent={(updates) => {
                if (canvasRef.current) {
                  canvasRef.current.updateComponent(updates);
                }
              }}
            />
          </div>
        </>
      ) : (
        // Mateixa disposició que el llenç causal des del 26/08/2026: la barra a dalt, el llenç a
        // tota l'amplada i les propietats flotant només quan hi ha alguna cosa triada. Abans hi
        // havia 256 px de paleta a l'esquerra —figures per arrossegar, tres plantilles i un
        // importador— i 320 px de panell a la dreta, sempre.
        <>
          <BarraEstocs mode={mode} onModeChange={handleModeChange} canvasRef={canvasRef}
            titolModel={currentModel.name} peuPDF={peuPDF} />
          <div className="flex-1 relative overflow-hidden flex">
            <ModelCanvas
              ref={canvasRef}
              modelId={id}
              onModeChange={setMode}
              onSelectComponent={setSelectedComponent}
              selectedComponent={selectedComponent}
            />
            <EnhancedPropertiesPanel
              flotant
              component={selectedComponent}
              components={currentModel.components || []}
              onTancar={() => setSelectedComponent(null)}
              onUpdateComponent={(updates) => {
                // Abans això era `setSelectedComponent(updates)` i prou: **editar una propietat
                // canviava el que es veia al panell i no arribava mai ni al llenç ni al model**.
                if (canvasRef.current) canvasRef.current.updateComponent(updates);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ModelEditor;